import React from "react";
import {BooleanRadioButton, BooleanRadioButtonGroup} from "@octopusdeploy/design-system-components";
import {DataBaseComponent, DataBaseComponentState, Errors} from "components/DataBaseComponent";
import repository from "client/repository";
import {BusyState} from "components/BusyFromPromise/BusyFromPromise";
import {HostedInstanceResource} from "client/resources/hostedInstanceResource";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import {Text} from "components/form";
import {validateCPU, validateMemory} from "areas/instances/Instances/k8sResourceValidation";
import {HostedInstanceConfigureLegacyComputeResourcesRequest} from "client/resources/hostedInstanceConfigureLegacyComputeResourcesRequest";
import { Callout } from "@octopusdeploy/design-system-components";

interface ConfigureLegacyComputeResourcesDialogProps {
    errors: Errors;
    busy: BusyState;
    instance: HostedInstanceResource;
    isModularExecutionsEnabled: boolean;
    onSaveClick(): Promise<boolean>;
}

interface ConfigureLegacyComputeResourcesDialogState extends DataBaseComponentState {
    request: Partial<HostedInstanceConfigureLegacyComputeResourcesRequest>;
}

export class ConfigureLegacyComputeResourcesDialog extends DataBaseComponent<ConfigureLegacyComputeResourcesDialogProps, ConfigureLegacyComputeResourcesDialogState> {
    constructor(props: ConfigureLegacyComputeResourcesDialogProps) {
        super(props);
        this.state = {
            request: {
                LegacyCPURequest: this.props.instance.LegacyCPURequest,
                LegacyCPULimit: this.props.instance.LegacyCPULimit,
                LegacyMemoryRequest: this.props.instance.LegacyMemoryRequest,
                LegacyMemoryLimit: this.props.instance.LegacyMemoryLimit,
                ApplyDuringOutageWindow: true
            }
        };
    }

    render() {
        const request = this.state.request;
        return <SaveDialogLayout title={"Configure Legacy Compute Resources"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={() => this.configureLegacyComputeResources(request)}>

            { this.props.isModularExecutionsEnabled &&
                <Callout type="warning" title="Warning">
                    This instance has Modular Executions enabled. Changes made here will have no effect until Modular Executions is disabled.
                </Callout> }

            <BooleanRadioButtonGroup value={request.ApplyDuringOutageWindow} onChange={ApplyDuringOutageWindow => this.updateRequest({ApplyDuringOutageWindow})}>
                <BooleanRadioButton value={true} label="Apply During Maintenance Window" />
                <BooleanRadioButton value={false} label="Apply Immediately" />
            </BooleanRadioButtonGroup>

            <Text label="Legacy CPU Request"
                  value={request.LegacyCPURequest}
                  onChange={LegacyCPURequest => this.updateRequest({LegacyCPURequest})}
                  validate={validateCPU}
                  hintText="CPU request for Legacy pod (e.g., 100m, 0.5)" />

            <Text label="Legacy CPU Limit"
                  value={request.LegacyCPULimit}
                  onChange={LegacyCPULimit => this.updateRequest({LegacyCPULimit})}
                  validate={validateCPU}
                  hintText="CPU limit for Legacy pod (e.g., 1000m, 1)" />

            <Text label="Legacy Memory Request"
                  value={request.LegacyMemoryRequest}
                  onChange={LegacyMemoryRequest => this.updateRequest({LegacyMemoryRequest})}
                  validate={validateMemory}
                  hintText="Memory request for Legacy pod (e.g., 800Mi, 1Gi)" />

            <Text label="Legacy Memory Limit"
                  value={request.LegacyMemoryLimit}
                  onChange={LegacyMemoryLimit => this.updateRequest({LegacyMemoryLimit})}
                  validate={validateMemory}
                  hintText="Memory limit for Legacy pod (e.g., 1Gi, 2G)" />

        </SaveDialogLayout>;
    }

    private updateRequest(update: Partial<HostedInstanceConfigureLegacyComputeResourcesRequest>) {
        return this.setState(prevState => ({request: {...prevState.request, ...update}}));
    }

    private configureLegacyComputeResources(request: Partial<HostedInstanceConfigureLegacyComputeResourcesRequest>) {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.configureLegacyComputeResources(this.props.instance, request as HostedInstanceConfigureLegacyComputeResourcesRequest);
            await this.props.onSaveClick();
            return true;
        });
    }
}
